import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  History,
  Check,
  X,
  IndianRupee,
  ArrowRight,
  Loader2,
  AlertCircle,
  Download,
  FileText
} from "lucide-react";
import { supabase } from "../lib/supabase";
import toast from "react-hot-toast";

export default function FeeManagement() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [amount, setAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchFinancialData();
  }, []);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("students")
        .select("id, full_name, course_name, total_fee, paid_fee, pending_balance")
        .order("full_name");

      if (error) throw error;
      setStudents(data || []);
    } catch (err) {
      toast.error("Error loading financial records");
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentHistory = async (student) => {
    setSelectedStudent(student);
    try {
      const { data, error } = await supabase
        .from("fee_payments")
      .select(`
    id,
    amount,
    payment_date,
    students (
      full_name,
      course_name
    )
  `)
        .eq("student_id", student.id)
        .order("payment_date", { ascending: false });

      if (error) throw error;
      setPaymentHistory(data || []);
      setShowHistoryModal(true);
    } catch (err) {
      toast.error("Could not fetch payment history");
    }
  };

 const handlePayment = async (e) => {
  e.preventDefault();
  const payAmount = Number(amount);

  if (!payAmount || payAmount <= 0) return toast.error("Enter a valid amount");
  if (payAmount > selectedStudent.pending_balance) {
    return toast.error(`Payment exceeds pending balance of ₹${selectedStudent.pending_balance}`);
  }

  try {
    setIsSubmitting(true);

    // 1. Record in History Table using the selected paymentDate
    const { error: historyError } = await supabase
      .from("fee_payments")
      .insert([{
        student_id: selectedStudent.id,
        amount: payAmount,
        course_name: selectedStudent.course_name,
        payment_date: paymentDate // Use the state variable here
      }]);

    if (historyError) throw historyError;

    // 2. Update Master Student Table
    const currentPaid = Number(selectedStudent.paid_fee || 0);
    const newPaidTotal = currentPaid + payAmount;

    const { error: updateError } = await supabase
      .from("students")
      .update({ paid_fee: newPaidTotal })
      .eq("id", selectedStudent.id);

    if (updateError) throw updateError;

    toast.success(`Collected ₹${payAmount} from ${selectedStudent.full_name}`);

    // 3. Print Receipt with the chosen date
    // handlePrint({ ...selectedStudent, paid_fee: newPaidTotal }, payAmount, paymentDate);

    // 4. Cleanup
    setShowPaymentModal(false);
    setAmount("");
    setPaymentDate(new Date().toISOString().split('T')[0]); // Reset to current date
    fetchFinancialData();
  } catch (err) {
    toast.error("Transaction failed. Check connection.");
  } finally {
    setIsSubmitting(false);
  }
};

 const handlePrint = (student, amountPaid, customDate = null) => {
  const printDate = customDate
    ? new Date(customDate).toLocaleDateString("en-IN")
    : new Date().toLocaleDateString("en-IN");

  // Generate a professional receipt number
  const receiptNo = `RCPT-${Date.now().toString().slice(-6)}`;

  // Safe numeric values
  const totalFee = Number(student.total_fee || 0);
  const pendingBalance = Number(student.pending_balance || 0);
  
   const totalPaid = Number(
    student.settled_amount ??
    (totalFee - pendingBalance)
  );
  const currentPayment = Number(amountPaid || 0);

  const printWindow = window.open("", "_blank");

  printWindow.document.write(`
    <html>
      <head>
        <title>Receipt - ${student.full_name}</title>
        <style>
          body {
            font-family: 'Segoe UI', Arial, sans-serif;
            padding: 30px;
            color: #1e293b;
            line-height: 1.5;
            background: #ffffff;
          }

          .receipt-container {
            max-width: 800px;
            margin: 0 auto;
            border: 2px solid #e2e8f0;
            border-radius: 12px;
            overflow: hidden;
          }

          .header {
            text-align: center;
            background: #f97316;
            color: white;
            padding: 25px 20px;
          }

          .header h1 {
            margin: 0;
            font-size: 30px;
            font-weight: 800;
            letter-spacing: 1px;
          }

          .header p {
            margin: 6px 0 0;
            font-size: 14px;
            opacity: 0.95;
          }

          .receipt-label {
            text-transform: uppercase;
            letter-spacing: 3px;
            font-weight: 900;
            margin-top: 8px;
            font-size: 13px;
          }

          .content {
            padding: 30px;
          }

          .top-meta {
            display: flex;
            justify-content: space-between;
            margin-bottom: 25px;
            font-size: 14px;
            font-weight: 600;
          }

          .section-title {
            font-size: 16px;
            font-weight: 700;
            color: #f97316;
            border-bottom: 2px solid #fed7aa;
            padding-bottom: 6px;
            margin-bottom: 15px;
          }

          .details {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px 24px;
            margin-bottom: 30px;
          }

          .row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #f1f5f9;
            gap: 10px;
          }

          .row span:first-child {
            color: #64748b;
            font-weight: 500;
          }

          .row strong {
            color: #0f172a;
            text-align: right;
          }

          .summary {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            padding: 20px;
            margin-bottom: 30px;
          }

          .summary .row:last-child {
            border-bottom: none;
            font-size: 18px;
            font-weight: 800;
          }

          .paid {
            color: #059669;
          }

          .pending {
            color: #dc2626;
          }

          .stamp {
            border: 5px solid #10b981;
            width: 120px;
            height: 120px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #10b981;
            font-weight: 900;
            font-size: 24px;
            transform: rotate(-15deg);
            margin: 20px auto;
            letter-spacing: 2px;
          }

          .signature {
            display: flex;
            justify-content: space-between;
            margin-top: 50px;
            font-size: 14px;
          }

          .signature-box {
            width: 200px;
            text-align: center;
          }

          .signature-line {
            border-top: 1px solid #334155;
            margin-top: 40px;
            padding-top: 6px;
            font-weight: 600;
          }

          .footer {
            text-align: center;
            font-size: 11px;
            color: #64748b;
            padding: 20px 30px 30px;
            border-top: 1px solid #e2e8f0;
          }

          @media print {
            body {
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          <div class="header">
            <h1>DIVYA COMPUTER INSTITUTE</h1>
            <p>Mardah Bazaar, Ghazipur, Uttar Pradesh</p>
            <p>Contact: +91 9792257501 | Website: www.divyacomputer.in</p>
            <div class="receipt-label">Official Fee Receipt</div>
          </div>

          <div class="content">
            <div class="top-meta">
              <div><strong>Receipt No:</strong> ${receiptNo}</div>
              <div><strong>Receipt Date:</strong> ${printDate}</div>
            </div>

            <div class="section-title">Student Information</div>
            <div class="details">
              <div class="row"><span> Name</span><strong>${student.full_name || "-"}</strong></div>
              <div class="row"><span>Course Name</span><strong>${student.course_name || "-"}</strong></div>
            </div>

            <div class="section-title">Fee Summary</div>
            <div class="summary">
              <div class="row">
                <span>Total Course Fee</span>
                <strong>₹${totalFee.toLocaleString("en-IN")}</strong>
              </div>
              <div class="row">
                <span>Total Fee Paid Till Date</span>
                <strong class="paid">₹${totalPaid.toLocaleString("en-IN")}</strong>
              </div>
              <div class="row">
                <span>Current Payment</span>
                <strong class="paid">₹${currentPayment.toLocaleString("en-IN")}</strong>
              </div>
              <div class="row">
                <span>Pending Balance</span>
                <strong class="pending">₹${pendingBalance.toLocaleString("en-IN")}</strong>
              </div>
            </div>

            <div class="stamp">PAID</div>

            <div class="signature">
              <div class="signature-box">
                <div class="signature-line">Student Signature</div>
              </div>
              <div class="signature-box">
                <div class="signature-line">Authorized Signature</div>
              </div>
            </div>
          </div>

          <div class="footer">
            <p>This is a computer-generated receipt and does not require a physical signature.</p>
            <p>Please keep this receipt for future reference.</p>
          </div>
        </div>
      </body>
    </html>
  `);

  printWindow.document.close();

  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 500);
};

  const filteredStudents = students.filter(
    (s) =>
      s.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.course_name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const totalCollected = students.reduce((a, b) => a + (Number(b.paid_fee) || 0), 0);
  const totalPending = students.reduce((a, b) => a + (Number(b.pending_balance) || 0), 0);

  return (
    <div className="space-y-6 animate-in fade-in duration-700 p-4">
      {/* --- Stats Summary --- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-[2rem] border border-orange-50 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-emerald-100 text-emerald-600 rounded-2xl"><IndianRupee size={24} /></div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Collected</p>
            <h3 className="text-xl font-black text-slate-900">₹{totalCollected.toLocaleString()}</h3>
          </div>
        </div>
        <div className="bg-white p-6 rounded-[2rem] border border-orange-50 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-rose-100 text-rose-600 rounded-2xl"><History size={24} /></div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Pending</p>
            <h3 className="text-xl font-black text-slate-900">₹{totalPending.toLocaleString()}</h3>
          </div>
        </div>
        <div className="bg-slate-900 p-6 rounded-[2rem] text-white flex items-center gap-4">
          <div className="p-3 bg-orange-500 rounded-2xl text-white"><Check size={24} /></div>
          <div>
            <p className="text-[10px] font-black text-white/40 uppercase tracking-widest">Recovery Rate</p>
            <h3 className="text-xl font-black">{students.length > 0 ? ((totalCollected / (totalCollected + totalPending)) * 100).toFixed(1) : 0}%</h3>
          </div>
        </div>
      </div>

      {/* --- Main Table --- */}
      <div className="bg-white rounded-[2rem] border border-orange-50 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-orange-50 flex flex-col md:flex-row justify-between items-center gap-4 bg-slate-50/30">
          <h2 className="text-xl font-black text-slate-800 tracking-tight italic">Fee Records 2026</h2>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
            <input
              type="text"
              placeholder="Search student..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:border-orange-500 outline-none font-bold text-sm"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-20 flex flex-col items-center"><Loader2 className="animate-spin text-orange-500 mb-2" /><p className="text-sm text-slate-400 italic">Syncing accounts...</p></div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-white text-[10px] font-black text-slate-400 uppercase border-b border-orange-50">
                  <th className="px-8 py-4 text-left">Student</th>
                  <th className="px-6 py-4 text-left">Course Fee</th>
                  <th className="px-6 py-4 text-left">Paid</th>
                  <th className="px-6 py-4 text-left">Balance</th>
                  <th className="px-8 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-orange-50/50">
                {filteredStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-orange-50/20 group">
                    <td className="px-8 py-4">
                      <p className="font-black text-slate-800 text-sm">{student.full_name}</p>
                      <p className="text-[10px] text-orange-500 font-bold uppercase">{student.course_name}</p>
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-slate-600">₹{student.total_fee}</td>
                    <td className="px-6 py-4 text-sm font-black text-emerald-600">₹{student.paid_fee || 0}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-lg text-[10px] font-black ${student.pending_balance > 0 ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>
                        ₹{student.pending_balance}
                      </span>
                    </td>
                    <td className="px-8 py-4 text-right flex justify-end gap-2">
                      <button onClick={() => fetchStudentHistory(student)} className="p-2 text-slate-400 hover:text-orange-500 transition-colors" title="Payment History">
                        <History size={18} />
                      </button>
                      <button
                        onClick={() => { setSelectedStudent(student); setShowPaymentModal(true); }}
                        className="px-4 py-2 bg-slate-900 text-white rounded-xl font-bold text-xs hover:bg-orange-500 transition-all shadow-md"
                      >
                        Record
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* --- History Modal --- */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl overflow-hidden border border-orange-50">
            <div className="p-6 bg-slate-900 text-white flex justify-between items-center">
              <div>
                <h3 className="text-xl font-black italic">Transaction History</h3>
                <p className="text-orange-400 text-[10px] font-black uppercase tracking-widest">{selectedStudent?.full_name}</p>
              </div>
              <button onClick={() => setShowHistoryModal(false)} className="p-2 bg-white/10 rounded-xl hover:bg-white/20"><X size={20} /></button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {paymentHistory.length === 0 ? (
                <div className="text-center py-10"><FileText className="mx-auto text-slate-200 mb-2" size={48} /><p className="text-slate-400 font-bold italic">No previous payments found.</p></div>
              ) : (
                <table className="w-full">
                  <thead className="text-[10px] uppercase font-black text-slate-400 border-b">
                    <tr><th className="text-left py-3">Date</th><th className="text-left py-3">Amount</th><th className="text-right py-3">Action</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {paymentHistory.map((pay) => (
                      <tr key={pay.id}>
                        <td className="py-4 text-sm font-bold text-slate-600">{new Date(pay.payment_date).toLocaleDateString("en-IN")}</td>
                        <td className="py-4 text-sm font-black text-emerald-600">₹{pay.amount}</td>
                        <td className="py-4 text-right">
                          <button onClick={() => handlePrint(selectedStudent, pay.amount, pay.payment_date)} className="text-orange-500 hover:text-orange-600 flex items-center justify-end gap-1 text-xs font-black">
                            <Download size={14} /> Receipt
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- Payment Modal --- */}
      {showPaymentModal && selectedStudent && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden border border-orange-50">
            <div className="p-6 bg-slate-900 text-white flex justify-between items-center">
              <div><h3 className="text-xl font-black italic">Collect Fees</h3><p className="text-orange-400 text-[10px] font-black uppercase">{selectedStudent.full_name}</p></div>
              <button onClick={() => setShowPaymentModal(false)} className="p-2 bg-white/10 rounded-xl hover:bg-white/20"><X size={20} /></button>
            </div>
            <form onSubmit={handlePayment} className="p-8 space-y-6">
              <div className="grid grid-cols-2 gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <div className="space-y-1"><span className="text-[9px] font-black text-slate-400 uppercase block">Total</span><span className="font-black text-slate-800">₹{selectedStudent.total_fee}</span></div>
                <div className="space-y-1 text-right"><span className="text-[9px] font-black text-slate-400 uppercase block">Paid</span><span className="font-black text-emerald-600">₹{selectedStudent.paid_fee || 0}</span></div>
                <div className="col-span-2 pt-2 border-t flex justify-between items-center mt-2">
                  <span className="text-[9px] font-black text-rose-500 uppercase">Balance</span>
                  <span className="text-lg font-black text-rose-600 underline">₹{selectedStudent.pending_balance}</span>
                </div>
              </div>

              <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Payment Date</label>
          <input
            type="date"
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 outline-none font-bold text-slate-700"
          />
        </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Payment Amount (₹)</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center text-orange-500 font-black">₹</div>
                  <input
                    type="number" required autoFocus min="1" max={selectedStudent.pending_balance}
                    placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)}
                    className="w-full pl-10 pr-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 outline-none font-black text-2xl"
                  />
                </div>
              </div>
              <button disabled={isSubmitting} className="w-full py-4 bg-orange-500 text-white rounded-2xl font-black text-md shadow-xl hover:bg-slate-900 transition-all flex items-center justify-center gap-3 disabled:opacity-50">
                {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : <><IndianRupee size={20} /> Collect </>}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}